import { useState } from "react";

import { DEFAULT_SHOW_SUCCESS_DURATION } from "ui/button/loading-button/loading-button.constants";
import { LoadingButtonState } from "ui/button/loading-button/loading-button.types";

type UseAsyncSubmitOptions<T> = {
  onSuccess?: (result: T) => void;
  onError?: (error: unknown) => void;
  successDuration?: number;
};

export const useAsyncSubmit = <T, A extends any[]>(
  submitFunction: (...args: A) => Promise<T>,
  options?: UseAsyncSubmitOptions<T>,
) => {
  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const submit = async (...args: A) => {
    setSubmitState(LoadingButtonState.Loading);
    try {
      const result = await submitFunction(...args);
      setSubmitState(LoadingButtonState.Success);

      if (options?.onSuccess) {
        setTimeout(() => {
          options.onSuccess?.(result);
        }, options?.successDuration ?? DEFAULT_SHOW_SUCCESS_DURATION);
      }
      return result;
    } catch (error) {
      setSubmitState(LoadingButtonState.Error);
      if (options?.onError) {
        options.onError(error);
      }
      throw error;
    }
  };

  return {
    submit,
    submitState,
    isSubmitting: submitState === LoadingButtonState.Loading,
  };
};
